import * as React from 'react';
import {transparentize} from 'polished';
import styled from 'styled-components';
import Button, {ButtonVariant} from 'components/Button';
import CourseInfoItem from '../components/CourseInfoItem';
import {
  BlockLabel as Label,
  BlockTitle,
  Column,
} from '../components/styled';
import {
  IconLessonCount,
  IconTaskCount,
  IconWebinarCount,
} from 'icons';
import {CoursePromoContext} from '../context';
import {ChapterItem} from '../types';
import {useState} from 'react';


const Container = styled.div`
  padding: 48px 60px 0;
  border-radius: 10px;

  @media screen and (max-width: 640px) {
    padding: 40px 16px 0;
  }
`;

const Header = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: flex-start;

  @media screen and (max-width: 900px) {
    flex-direction: column;
  }
`;

const ContentInfo = styled.div`
  display: grid;
  grid-template-columns: repeat(3, max-content);
  gap: 10px;
  
  @media screen and (max-width: 900px) {
    margin-top: -20px;
    margin-bottom: 20px;
  }
`;

const Title = styled(BlockTitle)`
  margin-bottom: 40px;
`;

const Content = styled.div`
`;

const Item = styled.div`
  padding: 18px 0;
  border: 1px solid ${({theme}) => `
    ${transparentize(.95, theme.colors.white)};
  `};
  border-left: none;
  border-right: none;
`;

const ItemLabel = styled.div`
  margin-bottom: 6px;
  font-size: 14px;
  line-height: 18px;
  color: ${({theme}) => `
    ${transparentize(.2, theme.colors.white)};
  `};
`;

const ItemTitle = styled.div`
  font-size: 18px;
  font-weight: 600;
  line-height: 23px;
`;

/**
 * The returns course program component
 * @return {React.ReactElement | null}
 * @constructor
 */
export default function Program(): React.ReactElement {
  const context = React.useContext(CoursePromoContext);

  const [isShowAll, setShowAll] = useState(false);

  /**
   * Render course program items
   * @constructor
   */
  function renderItems(): React.ReactNodeArray {
    const activeChapters: ChapterItem[] = context
      .chapters
      .filter(Boolean);

    return activeChapters.map(renderItem);
  }

  /**
   * Render course program item
   * @param {ChapterItem} title
   * @param {number} ind
   * @constructor
   */
  function renderItem(
      title: ChapterItem,
      ind: number,
  ): React.ReactElement | null {
    if (ind >= 5 && !isShowAll) return null;

    return (
      <Item key={ind.toString() + title}>
        <ItemLabel>{ind+1} глава</ItemLabel>
        <ItemTitle>{title}</ItemTitle>
      </Item>
    );
  }

  /**
   * Render course info item with course program count
   * @param {string} key
   * @constructor
   */
  function renderContentInfo(
      key: string,
  ): React.ReactElement | undefined {
    const count = context.contentCounters[key];
    if (count) {
      return (
        <CourseInfoItem
          title={getContentInfoTitle(count, key)}
          icon={getContentInfoIcon(key)}
        />
      );
    }
  }

  /**
   * the function return content info title
   * @param {number} count
   * @param {string} type
   * @constructor
   */
  function getContentInfoTitle(count: number, type: string): string {
    switch (type) {
      case 'lessons':
        return `${count} урока`;
      case 'tasks':
        return `${count} задания`;
      case 'webinars':
        return `${count} трансляций`;
      default:
        return '';
    }
  }

  /**
   * the function return content info icon
   * @param {string} type
   * @constructor
   */
  function getContentInfoIcon(type: string): React.ReactElement {
    switch (type) {
      case 'lessons':
        return <IconLessonCount/>;
      case 'tasks':
        return <IconTaskCount/>;
      case 'webinars':
        return <IconWebinarCount/>;
      default:
        return <div>...</div>;
    }
  }

  /**
   * the function render show all button
   * @return {React.ReactElement | null}
   * @constructor
   */
  function renderShowAllButton(): React.ReactElement | null {
    const chaptersLength = context
      .chapters
      .filter(Boolean)
      .length;

    if (!isShowAll && chaptersLength >= 5) {
      return (
        <Button
          onClick={() => setShowAll(true)}
          variant={ButtonVariant.Outline}
        >
          раскрыть всю программу
        </Button>
      );
    }

    return null;
  }

  return (
    <Container>
      <Header>
        <Column>
          <Label>Программа курса</Label>
          <Title>
            Что Вас ждет на курсе
          </Title>
        </Column>
        <ContentInfo>
          {renderContentInfo('lessons')}
          {renderContentInfo('tasks')}
          {renderContentInfo('webinars')}
        </ContentInfo>
      </Header>
      <Content>
        {renderItems()}
      </Content>
      {renderShowAllButton()}
    </Container>
  );
}
